// Pure helpers for the bias-override UI surface.
//
// These derive presentation state (status badges, time-until-expiry,
// chronological ordering) from the override row shape returned by
// /api/autonomy/bias-overrides. Extracted from EngagementBiasPanel
// so they can be unit-tested independently of React + Testing Library.
//
// Wave 10 follow-on: surfaces audit history + expiring-soon warnings
// for the operator on top of the Wave 9 write path.
// Wave 15: full TypeScript with shared domain types.

import type {
  BannerMessage,
  BiasOverride,
  BiasOverrideStatus,
  BiasOverrideWithStatus,
} from './types'

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

// Time-until-expiry threshold for the "expiring soon" warning. 24h
// matches the user's mental model of "warn me with a day's notice".
export const EXPIRING_SOON_MS = 24 * HOUR_MS

// Override input is intentionally permissive — callers may pass a row
// shape that's strictly Partial<BiasOverride> while loading. The pure
// helpers must handle null/undefined/missing fields without throwing.
type MaybeOverride = Partial<BiasOverride> | null | undefined

// Derive the operational status of an override at a given moment.
// Returns one of:
//   - 'active'         — unrevoked, not expired (or no expiry)
//   - 'expiring-soon'  — active AND will expire within EXPIRING_SOON_MS
//   - 'expired'        — has expiresAt and it's in the past
//   - 'revoked'        — was explicitly revoked by an operator
//
// `revoked` wins over `expired` when both apply, because the operator's
// explicit action is the more meaningful signal for audit reading.
export function getOverrideStatus(
  override: MaybeOverride,
  nowIso: string = new Date().toISOString()
): BiasOverrideStatus | null {
  if (!override) return null
  if (override.revokedAt) return 'revoked'
  const now = new Date(nowIso).getTime()
  if (!Number.isFinite(now)) return 'active'
  if (override.expiresAt) {
    const expires = new Date(override.expiresAt).getTime()
    if (Number.isFinite(expires)) {
      if (expires <= now) return 'expired'
      if (expires - now <= EXPIRING_SOON_MS) return 'expiring-soon'
    }
  }
  return 'active'
}

// Returns the time-until-expiry in milliseconds, or null when there's
// no expiry or it can't be parsed. Negative when the override is
// already past expiry.
export function getMsUntilExpiry(
  override: MaybeOverride,
  nowIso: string = new Date().toISOString()
): number | null {
  if (!override?.expiresAt) return null
  const now = new Date(nowIso).getTime()
  const expires = new Date(override.expiresAt).getTime()
  if (!Number.isFinite(now) || !Number.isFinite(expires)) return null
  return expires - now
}

// Human-friendly "expires in 6h" / "expires in 3d" / "expired 2h ago".
// Used in the inline pillar row meta line.
export function formatExpiresIn(
  override: MaybeOverride,
  nowIso: string = new Date().toISOString()
): string {
  const ms = getMsUntilExpiry(override, nowIso)
  if (ms == null) return ''
  const abs = Math.abs(ms)
  let label: string
  if (abs < HOUR_MS) {
    const minutes = Math.max(1, Math.round(abs / (60 * 1000)))
    label = `${minutes}m`
  } else if (abs < DAY_MS) {
    label = `${Math.max(1, Math.round(abs / HOUR_MS))}h`
  } else {
    label = `${Math.max(1, Math.round(abs / DAY_MS))}d`
  }
  return ms >= 0 ? `expires in ${label}` : `expired ${label} ago`
}

// Sort overrides by created_at DESC, with active overrides surfacing
// first within the same timestamp band. Useful for the audit history
// rail where the operator wants "what's live now" near the top.
export function sortHistory(
  overrides: BiasOverride[] = [],
  nowIso: string = new Date().toISOString()
): BiasOverrideWithStatus[] {
  const decorated = overrides.map((override) => ({
    override,
    status: getOverrideStatus(override, nowIso),
    createdMs: new Date(override.createdAt || 0).getTime() || 0,
  }))
  decorated.sort((a, b) => {
    // Active/expiring-soon first, then expired/revoked.
    const aLive = a.status === 'active' || a.status === 'expiring-soon'
    const bLive = b.status === 'active' || b.status === 'expiring-soon'
    if (aLive !== bLive) return aLive ? -1 : 1
    return b.createdMs - a.createdMs
  })
  return decorated.map((d) => ({ ...d.override, status: d.status }))
}

export interface BuildExpiryBannerOptions {
  dismissedIds?: Set<string>
  nowIso?: string
  pillarLabel?: (pillarId: string) => string
  // Wave 16 — when supplied, expiring/expired banners render a quick-
  // extend button that calls onExtend(overrideId, days). The pure helper
  // emits the descriptor; App.jsx wires the actual API call + state
  // refresh. extendingIds is the set of override IDs whose extend
  // request is in flight (button shows disabled / busy).
  onExtend?: (overrideId: string, days: number) => void
  extendDays?: number
  extendingIds?: Set<string>
}

// Build banner-shaped messages for any override that needs operator
// attention right now: expiring within EXPIRING_SOON_MS, or already
// expired with no follow-up action. Wave 14 — feeds the
// PersistentBanner system in App.jsx.
//
// `dismissedIds` is a Set of override IDs the operator has already
// acknowledged (persisted in localStorage so banners don't re-flood
// after a reload). Pass an empty Set to surface all of them.
export function buildExpiryBannerMessages(
  overrides: Array<Partial<BiasOverride>> = [],
  options: BuildExpiryBannerOptions = {}
): BannerMessage[] {
  const {
    dismissedIds = new Set<string>(),
    nowIso = new Date().toISOString(),
    pillarLabel = (id: string) => id,
    onExtend,
    extendDays = 7,
    extendingIds = new Set<string>(),
  } = options
  const messages: BannerMessage[] = []
  for (const override of overrides) {
    if (!override?.id) continue
    if (dismissedIds.has(override.id)) continue
    const status = getOverrideStatus(override, nowIso)
    if (status !== 'expired' && status !== 'expiring-soon') continue
    const subject = override.contentType
      ? `${pillarLabel(override.pillar || '')} · ${override.contentType}`
      : pillarLabel(override.pillar || '')
    const factor = Number(override.factor || 1).toFixed(2)
    const overrideId = override.id
    const action = onExtend
      ? {
          label: extendingIds.has(overrideId)
            ? 'Extending…'
            : `Extend ${extendDays}d`,
          onClick: () => onExtend(overrideId, extendDays),
          disabled: extendingIds.has(overrideId),
        }
      : undefined
    if (status === 'expired') {
      messages.push({
        id: `bias-override-expired:${override.id}`,
        text: `${subject} bias override (×${factor}) expired ${formatExpiresIn(
          override,
          nowIso
        )
          .replace('expires in ', '')
          .replace(
            'expired ',
            ''
          )} ago. The closed loop has fallen back to the data-driven signal — extend or accept.`,
        tone: 'warning',
        dismissable: true,
        ...(action ? { action } : {}),
      })
    } else {
      messages.push({
        id: `bias-override-expiring:${override.id}`,
        text: `${subject} bias override (×${factor}) ${formatExpiresIn(
          override,
          nowIso
        )} — extend now or let it decay back to data-driven.`,
        tone: 'info',
        dismissable: true,
        ...(action ? { action } : {}),
      })
    }
  }
  return messages
}

export const __test__ = { HOUR_MS, DAY_MS }
